import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { veiculoService } from '../../services/api';
import Loading from '../../components/common/Loading';

const RelatorioVeiculos = () => {
  const [filtros, setFiltros] = useState({
    dataInicio: '',
    dataFim: '',
    tipo: '',
    status: ''
  });

  // Buscar todos os veículos
  const { data: veiculosData, isLoading } = useQuery({
    queryKey: ['veiculos-relatorio', filtros],
    queryFn: () => veiculoService.listar({ 
      ...filtros, 
      limit: 1000 // Buscar todos para relatório
    }),
  });

  const veiculos = React.useMemo(() => veiculosData?.data?.data || [], [veiculosData]);

  // Calcular estatísticas gerais
  const estatisticas = React.useMemo(() => {
    if (!veiculos.length) return null;

    const totalVeiculos = veiculos.length;
    const veiculosAtivos = veiculos.filter(v => v.ativo).length;
    const veiculosInativos = totalVeiculos - veiculosAtivos;
    
    // Agrupar por tipo
    const porTipo = veiculos.reduce((acc, veiculo) => {
      acc[veiculo.tipo] = (acc[veiculo.tipo] || 0) + 1;
      return acc;
    }, {});

    // Calcular quilometragem total
    const kmTotal = veiculos.reduce((acc, veiculo) => {
      return acc + (veiculo.hodometro?.atual || 0);
    }, 0);

    // Calcular média de quilometragem
    const kmMedio = kmTotal / totalVeiculos;

    // Veículos com maior quilometragem
    const veiculosMaiorKm = veiculos
      .sort((a, b) => (b.hodometro?.atual || 0) - (a.hodometro?.atual || 0))
      .slice(0, 5);

    // Veículos com menor quilometragem
    const veiculosMenorKm = veiculos
      .sort((a, b) => (a.hodometro?.atual || 0) - (b.hodometro?.atual || 0))
      .slice(0, 5);

    return {
      totalVeiculos,
      veiculosAtivos,
      veiculosInativos,
      porTipo,
      kmTotal,
      kmMedio,
      veiculosMaiorKm,
      veiculosMenorKm
    };
  }, [veiculos]);

  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => ({ ...prev, [campo]: valor }));
  };

  const formatarKm = (km) => {
    return km ? `${km.toLocaleString('pt-BR')} km` : '0 km';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ativo':
        return 'bg-green-100 text-green-800';
      case 'manutencao':
        return 'bg-yellow-100 text-yellow-800';
      case 'inativo':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="p-6 space-y-6">
      {/* Cabeçalho */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Relatório de Veículos</h1>
        <p className="text-gray-600">Análise completa da frota de veículos</p>
      </div>

      {/* Filtros */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Filtros</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data Início
            </label>
            <input
              type="date"
              value={filtros.dataInicio}
              onChange={(e) => handleFiltroChange('dataInicio', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data Fim
            </label>
            <input
              type="date"
              value={filtros.dataFim}
              onChange={(e) => handleFiltroChange('dataFim', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo
            </label>
            <select
              value={filtros.tipo}
              onChange={(e) => handleFiltroChange('tipo', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos</option>
              <option value="tcb">TCB</option>
              <option value="van">Van</option>
              <option value="onibus">Ônibus</option>
              <option value="caminhao">Caminhão</option>
              <option value="moto">Moto</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={filtros.status}
              onChange={(e) => handleFiltroChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos</option>
              <option value="ativo">Ativo</option>
              <option value="manutencao">Manutenção</option>
              <option value="inativo">Inativo</option>
            </select>
          </div>
        </div>
      </div>

      {/* Estatísticas Gerais */}
      {estatisticas && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <h3 className="text-sm font-medium text-gray-500">Total de Veículos</h3>
            <p className="text-2xl font-bold text-blue-600">{estatisticas.totalVeiculos}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <h3 className="text-sm font-medium text-gray-500">Veículos Ativos</h3>
            <p className="text-2xl font-bold text-green-600">{estatisticas.veiculosAtivos}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <h3 className="text-sm font-medium text-gray-500">Quilometragem Total</h3>
            <p className="text-2xl font-bold text-purple-600">{formatarKm(estatisticas.kmTotal)}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <h3 className="text-sm font-medium text-gray-500">Média por Veículo</h3>
            <p className="text-2xl font-bold text-orange-600">{formatarKm(Math.round(estatisticas.kmMedio))}</p>
          </div>
        </div>
      )}

      {/* Gráficos */}
      {estatisticas && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Distribuição por Tipo */}
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Distribuição por Tipo</h3>
            <div className="space-y-3">
              {Object.entries(estatisticas.porTipo)
                .sort(([,a], [,b]) => b - a)
                .map(([tipo, quantidade]) => (
                  <div key={tipo} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 capitalize">{tipo}</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-32 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full" 
                          style={{ 
                            width: `${(quantidade / estatisticas.totalVeiculos) * 100}%` 
                          }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium text-gray-900 w-8 text-right">{quantidade}</span>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* Top 5 Maior Quilometragem */}
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Top 5 - Maior Quilometragem</h3>
            <div className="space-y-3">
              {estatisticas.veiculosMaiorKm.map((veiculo, index) => (
                <div key={veiculo._id} className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-500">#{index + 1}</span>
                    <span className="text-sm text-gray-900">{veiculo.placa}</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900">
                    {formatarKm(veiculo.hodometro?.atual)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Tabela Detalhada */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Relatório Detalhado</h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Placa
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Veículo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Hodômetro
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Motorista
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Última Atualização
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {veiculos.map((veiculo) => (
                <tr key={veiculo._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {veiculo.placa}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {veiculo.marca} {veiculo.modelo} ({veiculo.ano})
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                    {veiculo.tipo}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(veiculo.status)}`}>
                      {veiculo.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatarKm(veiculo.hodometro?.atual)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {veiculo.motorista?.nome || 'Não atribuído'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {veiculo.hodometro?.ultimaAtualizacao 
                      ? new Date(veiculo.hodometro.ultimaAtualizacao).toLocaleDateString('pt-BR')
                      : '-'
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RelatorioVeiculos;
